import { sha256 } from "@noble/hashes/sha2.js";
import JSZip from "jszip";
import {
  inlineRecoveredStrings,
  neutralizeClassMethod,
  removeClassFields,
  rewriteClassSymbols,
} from "./class-rewriter";
import { parseClassFile } from "./classfile";
import type { ClassInfo, ScanReport, ScanSession } from "./types";

const MAX_REBUILD_ENTRIES = 12_000;
const MAX_REBUILD_INPUT_BYTES = 320 * 1024 * 1024;
const MAX_SINGLE_ENTRY_BYTES = 96 * 1024 * 1024;
const SYMBOL_MAP_PATH = "META-INF/jarlab-symbol-map.json";
const AUDIT_PATH = "META-INF/jarlab-rebuild.json";
const MAX_PRESERVED_METADATA_BYTES = 4 * 1024 * 1024;

export type SymbolRewriteProfile =
  | "none"
  | "readable"
  | "obfuscate-safe"
  | "obfuscate-aggressive"
  | "restore";

export type DeadCodeMode = "none" | "safe" | "aggressive";

export type ArchiveEdit =
  | {
      id: string;
      type: "remove";
      path: string;
    }
  | {
      id: string;
      type: "rename";
      path: string;
      newPath: string;
    }
  | {
      id: string;
      type: "text";
      path: string;
      text: string;
    }
  | {
      id: string;
      type: "replace";
      path: string;
      bytes: Uint8Array;
      sourceName?: string;
    }
  | {
      id: string;
      type: "add";
      path: string;
      bytes: Uint8Array;
      sourceName?: string;
    }
  | {
      id: string;
      type: "neutralize";
      path: string;
      methodIndex: number;
      methodLabel: string;
    };

export interface RebuildOptions {
  profile: SymbolRewriteProfile;
  edits: ArchiveEdit[];
  reason?: string;
  inlineRecoveredStrings?: boolean;
  deadCodeMode?: DeadCodeMode;
}

export interface RebuildAuditEntry {
  operation: string;
  path: string;
  detail: string;
}

export interface RebuildValidation {
  valid: boolean;
  errors: string[];
  warnings: string[];
  entries: number;
  classesValidated: number;
  expandedBytes: number;
  signaturesRemoved: number;
  restorationMatched?: number;
  restorationExpected?: number;
}

export interface RebuildResult {
  bytes: Uint8Array;
  fileName: string;
  audit: RebuildAuditEntry[];
  validation: RebuildValidation;
  symbolChanges: number;
  stringsInlined: number;
  stringRecovery: {
    plaintextLiterals: number;
    recoveredValues: number;
    rewriteCandidates: number;
    structurallyEligible: number;
    rewritten: number;
    subsumed: number;
    skipped: number;
    analysisOnly: number;
  };
  deadClassesRemoved: number;
  deadFieldsRemoved: number;
  deadMethodsNeutralized: number;
  profile: SymbolRewriteProfile;
}

export interface DeadCodeCandidate {
  id: string;
  kind: "class" | "method";
  path: string;
  className: string;
  methodIndex?: number;
  methodLabel?: string;
  reason: string;
}

export interface DeadFieldCandidate {
  path: string;
  className: string;
  fieldName: string;
  descriptor: string;
}

interface WorkingEntry {
  path: string;
  origin: string | null;
  bytes: Uint8Array;
}

interface SymbolMap {
  schema: "jarlab-symbol-map/v1";
  profile: Exclude<SymbolRewriteProfile, "none" | "restore">;
  createdAt: string;
  classes: Array<{ original: string; rewritten: string }>;
  paths: Array<{ original: string; rewritten: string }>;
  originalEntryHashes: Record<string, string>;
  preservedMetadata: Array<{ path: string; base64: string }>;
}

function owned(bytes: Uint8Array) {
  const output = new Uint8Array(bytes.length);
  output.set(bytes);
  return output;
}

function bytesToHex(bytes: Uint8Array) {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function hash(bytes: Uint8Array) {
  return bytesToHex(sha256(bytes));
}

function encodeBase64(bytes: Uint8Array) {
  let output = "";
  for (let offset = 0; offset < bytes.length; offset += 0x8000) {
    output += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000));
  }
  return globalThis.btoa(output);
}

function decodeBase64(value: string) {
  const binary = globalThis.atob(value);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

export function normalizeArchivePath(path: string) {
  const normalized = path
    .replaceAll("\\", "/")
    .replace(/^\/+/, "")
    .replace(/\/+/g, "/");
  if (
    !normalized ||
    normalized.endsWith("/") ||
    normalized.includes("\0") ||
    normalized.split("/").some((part) => !part || part === "." || part === "..")
  ) {
    throw new Error("Archive paths must be relative and cannot contain empty, . or .. segments.");
  }
  return normalized;
}

function isSignaturePath(path: string) {
  return /^META-INF\/[^/]+\.(?:SF|RSA|DSA|EC)$/i.test(path);
}

function isTextResource(path: string, bytes: Uint8Array) {
  if (bytes.length > 2 * 1024 * 1024) return false;
  if (
    /\.(?:json|ya?ml|xml|properties|cfg|conf|toml|ini|txt|md|mf|services)$/i.test(
      path,
    ) ||
    /^META-INF\/services\//i.test(path)
  ) {
    return true;
  }
  const sample = bytes.subarray(0, Math.min(bytes.length, 16 * 1024));
  if (!sample.length) return true;
  let printable = 0;
  for (const byte of sample) {
    if (byte === 9 || byte === 10 || byte === 13 || (byte >= 32 && byte <= 126)) {
      printable += 1;
    }
  }
  return printable / sample.length >= 0.92;
}

function replaceWithPlaceholders(
  value: string,
  replacements: Array<[string, string]>,
) {
  const matches = replacements
    .filter(([from, to]) => from && from !== to && value.includes(from))
    .sort((left, right) => right[0].length - left[0].length);
  if (!matches.length) return value;
  const placeholders = matches.map(
    (_, index) => `\u0001JARLAB_SYMBOL_${index.toString(36)}\u0002`,
  );
  let output = value;
  matches.forEach(([from], index) => {
    let cursor = 0;
    let rewritten = "";
    while (cursor < output.length) {
      const found = output.indexOf(from, cursor);
      if (found < 0) {
        rewritten += output.slice(cursor);
        break;
      }
      const before = found > 0 ? output[found - 1] : "";
      const after = output[found + from.length] ?? "";
      const beforeIsIdentifier = /[A-Za-z0-9_$/.]/.test(before);
      const afterIsIdentifier = /[A-Za-z0-9_$]/.test(after);
      const descriptorPrefix = before === "L" && from.includes("/");
      if ((!beforeIsIdentifier || descriptorPrefix) && !afterIsIdentifier) {
        rewritten += output.slice(cursor, found) + placeholders[index];
        cursor = found + from.length;
      } else {
        rewritten += output.slice(cursor, found + 1);
        cursor = found + 1;
      }
    }
    output = rewritten;
  });
  matches.forEach(([, to], index) => {
    output = output.replaceAll(placeholders[index], to);
  });
  return output;
}

function symbolReplacements(mapping: Map<string, string>) {
  const replacements: Array<[string, string]> = [];
  for (const [original, rewritten] of mapping) {
    replacements.push([original, rewritten]);
    replacements.push([
      original.replaceAll("/", "."),
      rewritten.replaceAll("/", "."),
    ]);
  }
  return replacements;
}

function opaqueIdentifier(value: string) {
  const simple = value.split("$").at(-1) ?? value;
  return (
    /^[_$]*(?:[Il1|]{1,8}|[a-zA-Z]{1,2}\d*)$/.test(simple) ||
    /^[_$][A-Za-z0-9_$]{0,4}$/.test(simple) ||
    /^(?:[Il1_]{3,}|[a-f0-9]{12,})$/i.test(simple)
  );
}

function classCandidate(
  item: ClassInfo,
  profile: Exclude<SymbolRewriteProfile, "none" | "restore">,
  entryPoints: Set<string>,
) {
  if (
    item.simpleName === "module-info" ||
    item.simpleName === "package-info" ||
    item.name.includes("META-INF.versions.")
  ) {
    return false;
  }
  if (profile === "readable") return opaqueIdentifier(item.simpleName);
  if (profile === "obfuscate-safe" && entryPoints.has(item.name)) return false;
  return true;
}

function generatedSimpleName(
  profile: Exclude<SymbolRewriteProfile, "none" | "restore">,
  index: number,
) {
  if (profile === "readable") return `RecoveredClass${String(index + 1).padStart(3, "0")}`;
  if (profile === "obfuscate-safe") return `j${index.toString(36)}`;
  return `_${index.toString(36)}`;
}

function buildSymbolMapping(
  report: ScanReport,
  profile: Exclude<SymbolRewriteProfile, "none" | "restore">,
) {
  const entryPoints = new Set(
    report.entryPoints.map((item) => item.className.replaceAll("/", ".")),
  );
  const mapping = new Map<string, string>();
  const reserved = new Set(report.classes.map((item) => item.name.replaceAll(".", "/")));
  let sequence = 0;
  for (const item of report.classes) {
    if (!classCandidate(item, profile, entryPoints)) continue;
    const internal = item.name.replaceAll(".", "/");
    const slash = internal.lastIndexOf("/");
    const packageName = slash < 0 ? "" : internal.slice(0, slash + 1);
    let candidate = "";
    do {
      candidate = `${packageName}${generatedSimpleName(profile, sequence++)}`;
    } while (reserved.has(candidate));
    reserved.add(candidate);
    mapping.set(internal, candidate);
  }
  return mapping;
}

function normalizedClassName(value: string) {
  return value.replaceAll("/", ".").replace(/\.class$/i, "");
}

const DEAD_NAME_PATTERN =
  /(?:^|[$_.-])(?:dead|decoy|dummy|fake|junk|noise|opaque|trash|unused)(?:[$_.-]|$)/i;

const REFLECTIVE_CALLBACKS = new Set([
  "main",
  "run",
  "start",
  "initialize",
  "onInitialize",
  "onInitializeClient",
  "onInitializeServer",
  "onLoad",
  "onEnable",
  "onDisable",
  "premain",
  "agentmain",
  "readObject",
  "writeObject",
  "readResolve",
  "writeReplace",
]);

export function findDeadCodeCandidates(
  report: ScanReport,
  mode: DeadCodeMode,
): DeadCodeCandidate[] {
  if (mode === "none" || !report.entryPoints.length || !report.classes.length) {
    return [];
  }
  const classes = new Map(
    report.classes.map((item) => [normalizedClassName(item.name), item]),
  );
  const reachable = new Set<string>();
  const queue = report.entryPoints
    .map((item) => normalizedClassName(item.className))
    .filter((name) => classes.has(name));
  while (queue.length) {
    const name = queue.shift()!;
    if (reachable.has(name)) continue;
    reachable.add(name);
    const item = classes.get(name);
    if (!item) continue;
    for (const reference of item.referencedClasses) {
      const next = normalizedClassName(reference);
      if (classes.has(next) && !reachable.has(next)) queue.push(next);
    }
  }
  if (!reachable.size) return [];

  const candidates: DeadCodeCandidate[] = [];
  for (const item of report.classes) {
    const name = normalizedClassName(item.name);
    if (reachable.has(name)) continue;
    const synthetic = item.access.includes("synthetic");
    const namedDead = DEAD_NAME_PATTERN.test(item.simpleName);
    const substantive = item.methods.filter(
      (method) =>
        method.codeSize > 0 &&
        method.name !== "<init>" &&
        method.name !== "<clinit>",
    );
    const syntheticOnly =
      substantive.length > 0 &&
      substantive.every(
        (method) =>
          method.access.includes("synthetic") ||
          method.access.includes("bridge"),
      );
    if (
      mode !== "aggressive" &&
      !synthetic &&
      !namedDead &&
      !syntheticOnly
    ) {
      continue;
    }
    candidates.push({
      id: `class:${item.path}`,
      kind: "class",
      path: item.path,
      className: item.name,
      reason:
        mode === "aggressive"
          ? "Not reachable from a declared entry point"
          : namedDead
            ? "Unreachable class with an explicit dead/decoy identity"
            : "Unreachable compiler-synthetic class",
    });
  }

  const reachableCalls = new Set(
    report.crossReferences
      .filter((reference) => reference.reachable && !reference.external)
      .map(
        (reference) =>
          `${normalizedClassName(reference.toClass)}\u0000${reference.toMethod}`,
      ),
  );
  for (const item of report.classes) {
    const name = normalizedClassName(item.name);
    if (!reachable.has(name)) continue;
    for (let methodIndex = 0; methodIndex < item.methods.length; methodIndex += 1) {
      const method = item.methods[methodIndex];
      if (
        method.codeSize === 0 ||
        method.name === "<init>" ||
        method.name === "<clinit>" ||
        REFLECTIVE_CALLBACKS.has(method.name) ||
        reachableCalls.has(`${name}\u0000${method.name}`)
      ) {
        continue;
      }
      const synthetic =
        method.access.includes("synthetic") ||
        method.access.includes("bridge");
      const namedDead = DEAD_NAME_PATTERN.test(method.name);
      const aggressivePrivate =
        mode === "aggressive" && method.access.includes("private");
      if (!synthetic && !namedDead && !aggressivePrivate) continue;
      candidates.push({
        id: `method:${item.path}:${methodIndex}`,
        kind: "method",
        path: item.path,
        className: item.name,
        methodIndex,
        methodLabel: `${item.name}.${method.name}${method.descriptor}`,
        reason:
          mode === "aggressive" && aggressivePrivate
            ? "Private method has no reachable direct caller"
            : namedDead
              ? "Uncalled method has an explicit dead/decoy identity"
              : "Uncalled compiler-synthetic method",
      });
    }
  }
  return candidates.slice(0, 2_000);
}

export function findDeadFieldCandidates(
  report: ScanReport,
): DeadFieldCandidate[] {
  const candidates: DeadFieldCandidate[] = [];
  for (const classInfo of report.classes) {
    const referencedMembers = classInfo.methods
      .flatMap((method) => method.instructions)
      .map((instruction) => instruction.reference ?? "");
    for (const field of classInfo.fields) {
      if (
        /^bv\$[\w$]+$/.test(field.name) &&
        field.access.includes("private") &&
        field.access.includes("static") &&
        !referencedMembers.some((reference) =>
          reference.includes(`.${field.name}`),
        )
      ) {
        candidates.push({
          path: classInfo.path,
          className: classInfo.name,
          fieldName: field.name,
          descriptor: field.descriptor,
        });
      }
    }
  }
  return candidates.slice(0, 4_000);
}

function classPathMap(report: ScanReport, mapping: Map<string, string>) {
  const paths = new Map<string, string>();
  for (const item of report.classes) {
    const original = item.name.replaceAll(".", "/");
    const rewritten = mapping.get(original);
    if (!rewritten) continue;
    const suffix = `${original}.class`;
    paths.set(
      item.path,
      item.path.endsWith(suffix)
        ? `${item.path.slice(0, -suffix.length)}${rewritten}.class`
        : `${rewritten}.class`,
    );
  }
  return paths;
}

function sanitizeManifest(bytes: Uint8Array) {
  const text = new TextDecoder("utf-8", { fatal: false }).decode(bytes);
  const sections = text.replace(/\r\n/g, "\n").split(/\n\n+/);
  const kept: string[] = [];
  for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex += 1) {
    const lines = sections[sectionIndex].split("\n");
    const filtered: string[] = [];
    let skipContinuation = false;
    for (const line of lines) {
      if (/^[ \t]/.test(line)) {
        if (!skipContinuation) filtered.push(line);
        continue;
      }
      skipContinuation =
        /^(?:Signature-Version|Magic|.+-Digest(?:-.+)?):/i.test(line);
      if (!skipContinuation) filtered.push(line);
    }
    const hasDigest = lines.some((line) => /-Digest(?:-.+)?:/i.test(line));
    const namedSection = lines.some((line) => /^Name:/i.test(line));
    if (sectionIndex > 0 && namedSection && hasDigest) continue;
    if (filtered.some((line) => line.trim())) kept.push(filtered.join("\r\n"));
  }
  return new TextEncoder().encode(`${kept.join("\r\n\r\n").trimEnd()}\r\n`);
}

function editDescription(edit: ArchiveEdit) {
  if (edit.type === "remove") return "Removed archive entry";
  if (edit.type === "rename") return `Renamed to ${edit.newPath}`;
  if (edit.type === "text") return `Replaced text with ${edit.text.length.toLocaleString()} characters`;
  if (edit.type === "replace")
    return `Replaced bytes from ${edit.sourceName ?? "local input"}`;
  if (edit.type === "add")
    return `Added ${edit.bytes.length.toLocaleString()} bytes from ${edit.sourceName ?? "local input"}`;
  return `Neutralized ${edit.methodLabel}`;
}

async function loadWorkingEntries(report: ScanReport, session: ScanSession) {
  const files = report.entries.filter((entry) => !entry.directory);
  if (files.length > MAX_REBUILD_ENTRIES) {
    throw new Error(
      `The archive has ${files.length.toLocaleString()} files; rebuilding is capped at ${MAX_REBUILD_ENTRIES.toLocaleString()}.`,
    );
  }
  const total = files.reduce((sum, entry) => sum + entry.size, 0);
  if (total > MAX_REBUILD_INPUT_BYTES) {
    throw new Error("The expanded archive exceeds the 320 MB rebuild limit.");
  }
  const entries = new Map<string, WorkingEntry>();
  for (let index = 0; index < files.length; index += 1) {
    const item = files[index];
    if (item.size > MAX_SINGLE_ENTRY_BYTES) {
      throw new Error(`${item.path} exceeds the 96 MB per-entry rebuild limit.`);
    }
    const bytes = await session.readEntry(item.path);
    if (!bytes) throw new Error(`Could not read ${item.path}.`);
    entries.set(item.path, {
      path: item.path,
      origin: item.path,
      bytes: owned(bytes),
    });
    if (index % 24 === 23) {
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
    }
  }
  return entries;
}

function findByOrigin(entries: Map<string, WorkingEntry>, path: string) {
  return (
    entries.get(path) ??
    [...entries.values()].find((entry) => entry.origin === path) ??
    null
  );
}

function moveEntry(
  entries: Map<string, WorkingEntry>,
  entry: WorkingEntry,
  nextPath: string,
) {
  const normalized = normalizeArchivePath(nextPath);
  const existing = entries.get(normalized);
  if (existing && existing !== entry) {
    throw new Error(`The rebuild would create a duplicate path: ${normalized}`);
  }
  entries.delete(entry.path);
  entry.path = normalized;
  entries.set(normalized, entry);
}

function applyEdits(
  entries: Map<string, WorkingEntry>,
  edits: ArchiveEdit[],
  audit: RebuildAuditEntry[],
) {
  for (const edit of edits) {
    const path = normalizeArchivePath(edit.path);
    if (edit.type === "add") {
      if (entries.has(path)) throw new Error(`${path} already exists.`);
      entries.set(path, { path, origin: null, bytes: owned(edit.bytes) });
    } else {
      const entry = findByOrigin(entries, path);
      if (!entry) throw new Error(`${path} is no longer present in the rebuild.`);
      if (edit.type === "remove") {
        entries.delete(entry.path);
      } else if (edit.type === "rename") {
        moveEntry(entries, entry, edit.newPath);
      } else if (edit.type === "text") {
        entry.bytes = new TextEncoder().encode(edit.text);
      } else if (edit.type === "replace") {
        entry.bytes = owned(edit.bytes);
      } else if (edit.type === "neutralize") {
        entry.bytes = neutralizeClassMethod(entry.bytes, edit.methodIndex).bytes;
      }
    }
    audit.push({
      operation: edit.type,
      path,
      detail: editDescription(edit),
    });
  }
}

function applyRecoveredStrings(
  entries: Map<string, WorkingEntry>,
  report: ScanReport,
  audit: RebuildAuditEntry[],
) {
  const byEntry = new Map<string, ScanReport["recoveredExpressions"]>();
  for (const expression of report.recoveredExpressions) {
    if (!expression.patchable) continue;
    const values = byEntry.get(expression.entry) ?? [];
    values.push(expression);
    byEntry.set(expression.entry, values);
  }
  let attempted = 0;
  let eligible = 0;
  let applied = 0;
  let subsumed = 0;
  let skipped = 0;
  for (const classInfo of report.classes) {
    const recovered = byEntry.get(classInfo.path);
    if (!recovered?.length) continue;
    const entry = findByOrigin(entries, classInfo.path);
    if (!entry) continue;
    try {
      const rewritten = inlineRecoveredStrings(
        entry.bytes,
        classInfo,
        recovered,
      );
      entry.bytes = rewritten.bytes;
      attempted += rewritten.attempted;
      eligible += rewritten.eligible;
      applied += rewritten.applied;
      subsumed += rewritten.subsumed;
      skipped += rewritten.skipped;
      if (rewritten.applied) {
        audit.push({
          operation: "inline-recovered-strings",
          path: classInfo.path,
          detail: `Inlined ${rewritten.applied.toLocaleString()} constant decrypt call${rewritten.applied === 1 ? "" : "s"} without changing bytecode offsets`,
        });
      }
    } catch {
      attempted += recovered.length;
      skipped += recovered.length;
    }
  }
  if (skipped) {
    audit.push({
      operation: "inline-recovered-strings",
      path: "(archive)",
      detail: `Skipped ${skipped.toLocaleString()} unsafe or structurally ambiguous string replacement${skipped === 1 ? "" : "s"}`,
    });
  }
  if (subsumed) {
    audit.push({
      operation: "inline-recovered-strings",
      path: "(archive)",
      detail: `Folded ${subsumed.toLocaleString()} nested recovered value${subsumed === 1 ? "" : "s"} into wider constant replacements`,
    });
  }
  return { attempted, eligible, applied, subsumed, skipped };
}

function applyDeadCodePruning(
  entries: Map<string, WorkingEntry>,
  report: ScanReport,
  mode: DeadCodeMode,
  audit: RebuildAuditEntry[],
) {
  const candidates = findDeadCodeCandidates(report, mode);
  let classesRemoved = 0;
  let fieldsRemoved = 0;
  let methodsNeutralized = 0;
  const removedPaths = new Set<string>();
  for (const candidate of candidates.filter((item) => item.kind === "class")) {
    const entry = findByOrigin(entries, candidate.path);
    if (!entry) continue;
    entries.delete(entry.path);
    removedPaths.add(candidate.path);
    classesRemoved += 1;
    audit.push({
      operation: "dead-class-removal",
      path: candidate.path,
      detail: candidate.reason,
    });
  }
  for (const candidate of candidates.filter((item) => item.kind === "method")) {
    if (
      removedPaths.has(candidate.path) ||
      candidate.methodIndex === undefined
    ) {
      continue;
    }
    const entry = findByOrigin(entries, candidate.path);
    if (!entry) continue;
    try {
      entry.bytes = neutralizeClassMethod(
        entry.bytes,
        candidate.methodIndex,
      ).bytes;
      methodsNeutralized += 1;
      audit.push({
        operation: "dead-method-removal",
        path: candidate.path,
        detail: `${candidate.methodLabel}: ${candidate.reason}`,
      });
    } catch {
      audit.push({
        operation: "dead-method-skip",
        path: candidate.path,
        detail: `${candidate.methodLabel ?? "Method"} could not be safely neutralized`,
      });
    }
  }
  const fieldsByPath = new Map<string, string[]>();
  for (const candidate of findDeadFieldCandidates(report)) {
    const names = fieldsByPath.get(candidate.path) ?? [];
    names.push(candidate.fieldName);
    fieldsByPath.set(candidate.path, names);
  }
  for (const classInfo of report.classes) {
    if (removedPaths.has(classInfo.path)) continue;
    const fieldNames = fieldsByPath.get(classInfo.path) ?? [];
    if (!fieldNames.length) continue;
    const entry = findByOrigin(entries, classInfo.path);
    if (!entry) continue;
    try {
      const pruned = removeClassFields(entry.bytes, fieldNames);
      if (!pruned.removed) continue;
      entry.bytes = pruned.bytes;
      fieldsRemoved += pruned.removed;
      audit.push({
        operation: "dead-field-removal",
        path: classInfo.path,
        detail: `Removed ${pruned.removed.toLocaleString()} private unreferenced synthetic field declaration${pruned.removed === 1 ? "" : "s"}`,
      });
    } catch {
      audit.push({
        operation: "dead-field-skip",
        path: classInfo.path,
        detail:
          "Skipped synthetic field removal because the rewritten class did not validate",
      });
    }
  }
  return { classesRemoved, fieldsRemoved, methodsNeutralized };
}

function parseSymbolMap(bytes: Uint8Array): SymbolMap {
  const parsed = JSON.parse(
    new TextDecoder("utf-8", { fatal: false }).decode(bytes),
  ) as Partial<SymbolMap>;
  if (
    parsed.schema !== "jarlab-symbol-map/v1" ||
    !Array.isArray(parsed.classes) ||
    !Array.isArray(parsed.paths) ||
    !parsed.originalEntryHashes
  ) {
    throw new Error("The embedded JarLab symbol map is malformed.");
  }
  return parsed as SymbolMap;
}

function preserveMetadata(entries: Map<string, WorkingEntry>) {
  const targets = [...entries.values()].filter(
    (entry) =>
      isSignaturePath(entry.path) ||
      /^META-INF\/MANIFEST\.MF$/i.test(entry.path) ||
      entry.path === AUDIT_PATH ||
      entry.path === SYMBOL_MAP_PATH,
  );
  let budget = 0;
  const preserved: SymbolMap["preservedMetadata"] = [];
  for (const entry of targets) {
    budget += entry.bytes.length;
    if (budget > MAX_PRESERVED_METADATA_BYTES) break;
    preserved.push({ path: entry.path, base64: encodeBase64(entry.bytes) });
  }
  return preserved;
}

function originalHashes(entries: Map<string, WorkingEntry>) {
  return Object.fromEntries(
    [...entries.values()]
      .sort((left, right) => left.path.localeCompare(right.path))
      .map((entry) => [entry.path, hash(entry.bytes)]),
  );
}

function applySymbolRewrite(
  entries: Map<string, WorkingEntry>,
  report: ScanReport,
  profile: Exclude<SymbolRewriteProfile, "none" | "restore">,
  audit: RebuildAuditEntry[],
) {
  const mapping = buildSymbolMapping(report, profile);
  if (!mapping.size) {
    audit.push({
      operation: "symbol-rewrite",
      path: "(archive)",
      detail: "No class names met this profile's rewrite criteria",
    });
    return 0;
  }
  const pathMapping = classPathMap(report, mapping);
  const replacements = symbolReplacements(mapping);
  const originalEntryHashes = originalHashes(entries);
  const preservedMetadata = preserveMetadata(entries);
  let changes = 0;

  for (const entry of [...entries.values()]) {
    if (entry.path === SYMBOL_MAP_PATH) continue;
    if (entry.path.toLowerCase().endsWith(".class")) {
      const rewritten = rewriteClassSymbols(entry.bytes, (value) =>
        replaceWithPlaceholders(value, replacements),
      );
      entry.bytes = rewritten.bytes;
      changes += rewritten.changedConstants;
    } else if (isTextResource(entry.path, entry.bytes)) {
      const original = new TextDecoder("utf-8", { fatal: false }).decode(entry.bytes);
      const rewritten = replaceWithPlaceholders(original, replacements);
      if (rewritten !== original) {
        entry.bytes = new TextEncoder().encode(rewritten);
        changes += 1;
      }
    }

    const mappedPath = entry.origin ? pathMapping.get(entry.origin) : undefined;
    if (mappedPath) {
      moveEntry(entries, entry, mappedPath);
      changes += 1;
    } else if (/^META-INF\/services\//i.test(entry.path)) {
      const service = entry.path.slice("META-INF/services/".length).replaceAll(".", "/");
      const rewritten = mapping.get(service);
      if (rewritten) {
        moveEntry(
          entries,
          entry,
          `META-INF/services/${rewritten.replaceAll("/", ".")}`,
        );
        changes += 1;
      }
    }
  }

  const symbolMap: SymbolMap = {
    schema: "jarlab-symbol-map/v1",
    profile,
    createdAt: new Date().toISOString(),
    classes: [...mapping].map(([original, rewritten]) => ({
      original,
      rewritten,
    })),
    paths: [...pathMapping].map(([original, rewritten]) => ({
      original,
      rewritten,
    })),
    originalEntryHashes,
    preservedMetadata,
  };
  entries.set(SYMBOL_MAP_PATH, {
    path: SYMBOL_MAP_PATH,
    origin: null,
    bytes: new TextEncoder().encode(JSON.stringify(symbolMap, null, 2)),
  });
  audit.push({
    operation: "symbol-rewrite",
    path: "(archive)",
    detail: `${profile} rewrote ${mapping.size.toLocaleString()} class identities and ${changes.toLocaleString()} constants or paths`,
  });
  return changes;
}

function applySymbolRestore(
  entries: Map<string, WorkingEntry>,
  audit: RebuildAuditEntry[],
) {
  const mapEntry = entries.get(SYMBOL_MAP_PATH);
  if (!mapEntry) {
    throw new Error("This archive does not contain a JarLab reversible symbol map.");
  }
  const symbolMap = parseSymbolMap(mapEntry.bytes);
  const reverseMapping = new Map(
    symbolMap.classes.map((item) => [item.rewritten, item.original]),
  );
  const reversePaths = new Map(
    symbolMap.paths.map((item) => [item.rewritten, item.original]),
  );
  const replacements = symbolReplacements(reverseMapping);
  let changes = 0;

  entries.delete(SYMBOL_MAP_PATH);
  entries.delete(AUDIT_PATH);
  for (const entry of [...entries.values()]) {
    if (entry.path.toLowerCase().endsWith(".class")) {
      const restored = rewriteClassSymbols(entry.bytes, (value) =>
        replaceWithPlaceholders(value, replacements),
      );
      entry.bytes = restored.bytes;
      changes += restored.changedConstants;
    } else if (isTextResource(entry.path, entry.bytes)) {
      const original = new TextDecoder("utf-8", { fatal: false }).decode(entry.bytes);
      const restored = replaceWithPlaceholders(original, replacements);
      if (restored !== original) {
        entry.bytes = new TextEncoder().encode(restored);
        changes += 1;
      }
    }
    const originalPath = reversePaths.get(entry.path);
    if (originalPath) {
      moveEntry(entries, entry, originalPath);
      changes += 1;
    } else if (/^META-INF\/services\//i.test(entry.path)) {
      const service = entry.path.slice("META-INF/services/".length).replaceAll(".", "/");
      const restored = reverseMapping.get(service);
      if (restored) {
        moveEntry(
          entries,
          entry,
          `META-INF/services/${restored.replaceAll("/", ".")}`,
        );
        changes += 1;
      }
    }
  }

  for (const item of symbolMap.preservedMetadata ?? []) {
    entries.set(item.path, {
      path: item.path,
      origin: item.path,
      bytes: decodeBase64(item.base64),
    });
  }
  audit.push({
    operation: "symbol-restore",
    path: "(archive)",
    detail: `Restored ${symbolMap.classes.length.toLocaleString()} mapped class identities`,
  });
  return { changes, symbolMap };
}

function stripInvalidSignatures(
  entries: Map<string, WorkingEntry>,
  audit: RebuildAuditEntry[],
) {
  let removed = 0;
  for (const entry of [...entries.values()]) {
    if (isSignaturePath(entry.path) || /^META-INF\/INDEX\.LIST$/i.test(entry.path)) {
      entries.delete(entry.path);
      removed += 1;
    }
  }
  const manifest = [...entries.values()].find((entry) =>
    /^META-INF\/MANIFEST\.MF$/i.test(entry.path),
  );
  if (manifest) manifest.bytes = sanitizeManifest(manifest.bytes);
  if (removed) {
    audit.push({
      operation: "signature-cleanup",
      path: "META-INF",
      detail: `Removed ${removed.toLocaleString()} invalidated signature or index files and stripped manifest digests`,
    });
  }
  return removed;
}

async function packageEntries(entries: Map<string, WorkingEntry>) {
  const archive = new JSZip();
  for (const entry of [...entries.values()].sort((left, right) =>
    left.path.localeCompare(right.path),
  )) {
    archive.file(entry.path, owned(entry.bytes), {
      binary: true,
      createFolders: true,
      date: new Date("2000-01-01T00:00:00.000Z"),
    });
  }
  const generated = await archive.generateAsync({
    type: "uint8array",
    compression: "DEFLATE",
    compressionOptions: { level: 6 },
    platform: "UNIX",
  });
  return owned(generated);
}

export async function validateRebuiltArchive(
  bytes: Uint8Array,
  signaturesRemoved = 0,
  expectedHashes?: Record<string, string>,
): Promise<RebuildValidation> {
  const errors: string[] = [];
  const warnings: string[] = [];
  let archive: JSZip;
  try {
    archive = await JSZip.loadAsync(bytes, {
      checkCRC32: true,
      createFolders: true,
    });
  } catch (error) {
    return {
      valid: false,
      errors: [
        error instanceof Error ? error.message : "The rebuilt ZIP cannot be reopened.",
      ],
      warnings,
      entries: 0,
      classesValidated: 0,
      expandedBytes: 0,
      signaturesRemoved,
    };
  }

  const files = Object.values(archive.files).filter((entry) => !entry.dir);
  let expandedBytes = 0;
  let classesValidated = 0;
  let restorationMatched = 0;
  for (const file of files) {
    try {
      normalizeArchivePath(file.name);
    } catch (error) {
      errors.push(error instanceof Error ? error.message : `Unsafe path ${file.name}`);
      continue;
    }
    const data = await file.async("uint8array");
    expandedBytes += data.length;
    if (file.name.toLowerCase().endsWith(".class")) {
      try {
        const parsed = parseClassFile(data, file.name);
        const expectedSuffix = `${parsed.name.replaceAll(".", "/")}.class`;
        if (
          parsed.simpleName !== "module-info" &&
          !file.name.endsWith(expectedSuffix)
        ) {
          errors.push(
            `${file.name} declares ${parsed.name}; the class path and identity do not match.`,
          );
        }
        classesValidated += 1;
      } catch (error) {
        errors.push(
          `${file.name}: ${
            error instanceof Error ? error.message : "Class parsing failed"
          }`,
        );
      }
    }
    if (expectedHashes?.[file.name] && hash(data) === expectedHashes[file.name]) {
      restorationMatched += 1;
    }
    if (files.length % 50 === 0) {
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
    }
  }

  const manifestEntry =
    archive.file("META-INF/MANIFEST.MF") ??
    archive.file("meta-inf/manifest.mf");
  if (manifestEntry) {
    const manifest = await manifestEntry.async("string");
    const main = manifest.match(/^Main-Class:\s*(.+)$/im)?.[1]?.trim();
    if (main) {
      const path = `${main.replaceAll(".", "/")}.class`;
      if (!archive.file(path)) {
        errors.push(`Manifest Main-Class does not resolve to ${path}.`);
      }
    }
  }

  if (!classesValidated) warnings.push("No JVM classes were available to validate.");
  if (expandedBytes > MAX_REBUILD_INPUT_BYTES) {
    errors.push("The rebuilt archive exceeds the 320 MB expanded-size limit.");
  }
  return {
    valid: errors.length === 0,
    errors: errors.slice(0, 200),
    warnings,
    entries: files.length,
    classesValidated,
    expandedBytes,
    signaturesRemoved,
    restorationMatched: expectedHashes ? restorationMatched : undefined,
    restorationExpected: expectedHashes
      ? Object.keys(expectedHashes).length
      : undefined,
  };
}

function outputName(fileName: string, profile: SymbolRewriteProfile) {
  const base = fileName.replace(/\.(?:jar|zip)$/i, "");
  if (profile === "restore") return `${base}-restored.jar`;
  if (profile === "readable") return `${base}-deobfuscated.jar`;
  if (profile.startsWith("obfuscate")) return `${base}-obfuscated.jar`;
  return `${base}-rebuilt.jar`;
}

export async function buildRepairedArchive(
  report: ScanReport,
  session: ScanSession,
  options: RebuildOptions,
): Promise<RebuildResult> {
  const entries = await loadWorkingEntries(report, session);
  const audit: RebuildAuditEntry[] = [];
  const shouldInlineStrings =
    options.inlineRecoveredStrings ?? options.profile === "readable";
  const deadCodeMode = options.deadCodeMode ?? "none";
  const stringRewrite = shouldInlineStrings
    ? applyRecoveredStrings(entries, report, audit)
    : { attempted: 0, eligible: 0, applied: 0, subsumed: 0, skipped: 0 };
  const stringsInlined = stringRewrite.applied;
  const plaintextLiterals = report.classes.reduce(
    (total, classInfo) =>
      total +
      classInfo.methods.reduce(
        (methodTotal, method) =>
          methodTotal +
          method.instructions.filter(
            (instruction) => typeof instruction.literal === "string",
          ).length,
        0,
      ),
    0,
  );
  const recoveredValues = report.recoveredExpressions.filter(
    (item) => item.confidence === "high",
  ).length;
  const declaredRewriteCandidates = report.recoveredExpressions.filter(
    (item) => item.confidence === "high" && item.patchable,
  ).length;
  const stringRecovery = {
    plaintextLiterals,
    recoveredValues,
    rewriteCandidates: shouldInlineStrings ? declaredRewriteCandidates : 0,
    structurallyEligible: stringRewrite.eligible,
    rewritten: stringsInlined,
    subsumed: stringRewrite.subsumed,
    skipped: stringRewrite.skipped,
    analysisOnly: Math.max(0, recoveredValues - declaredRewriteCandidates),
  };
  applyEdits(entries, options.edits, audit);
  const deadCode =
    deadCodeMode === "none"
      ? { classesRemoved: 0, fieldsRemoved: 0, methodsNeutralized: 0 }
      : applyDeadCodePruning(entries, report, deadCodeMode, audit);
  let symbolChanges = 0;
  let restorationMap: SymbolMap | undefined;

  if (options.profile === "restore") {
    const restored = applySymbolRestore(entries, audit);
    symbolChanges = restored.changes;
    restorationMap = restored.symbolMap;
  } else if (options.profile !== "none") {
    symbolChanges = applySymbolRewrite(entries, report, options.profile, audit);
  }

  const contentChanged =
    options.edits.length > 0 ||
    options.profile !== "none" ||
    stringsInlined > 0 ||
    deadCode.classesRemoved > 0 ||
    deadCode.fieldsRemoved > 0 ||
    deadCode.methodsNeutralized > 0;
  let signaturesRemoved = 0;
  if (contentChanged && options.profile !== "restore") {
    signaturesRemoved = stripInvalidSignatures(entries, audit);
  }

  if (options.profile !== "restore") {
    const auditRecord = {
      schema: "jarlab-rebuild-audit/v1",
      createdAt: new Date().toISOString(),
      source: {
        fileName: report.fileName,
        sha256: report.hashes.sha256,
      },
      reason: options.reason?.trim().slice(0, 500) || "Local analyst rebuild",
      profile: options.profile,
      deobfuscation: {
        stringsInlined,
        stringRecovery,
        deadCodeMode,
        deadClassesRemoved: deadCode.classesRemoved,
        deadFieldsRemoved: deadCode.fieldsRemoved,
        deadMethodsNeutralized: deadCode.methodsNeutralized,
      },
      operations: audit,
      safety: {
        codeExecuted: false,
        signaturesRemoved,
        structuralValidationRequired: true,
      },
    };
    entries.set(AUDIT_PATH, {
      path: AUDIT_PATH,
      origin: null,
      bytes: new TextEncoder().encode(JSON.stringify(auditRecord, null, 2)),
    });
  }

  if (entries.size > MAX_REBUILD_ENTRIES) {
    throw new Error("The rebuilt archive exceeds the 12,000-entry limit.");
  }
  const output = await packageEntries(entries);
  const validation = await validateRebuiltArchive(
    output,
    signaturesRemoved,
    restorationMap?.originalEntryHashes,
  );
  if (
    options.profile === "restore" &&
    validation.restorationExpected !== undefined &&
    validation.restorationMatched !== validation.restorationExpected
  ) {
    validation.warnings.push(
      `${validation.restorationMatched ?? 0} of ${validation.restorationExpected} original entry hashes were restored exactly.`,
    );
  }

  return {
    bytes: output,
    fileName: outputName(report.fileName, options.profile),
    audit,
    validation,
    symbolChanges,
    stringsInlined,
    stringRecovery,
    deadClassesRemoved: deadCode.classesRemoved,
    deadFieldsRemoved: deadCode.fieldsRemoved,
    deadMethodsNeutralized: deadCode.methodsNeutralized,
    profile: options.profile,
  };
}
